import { apiClient } from './api';
import {
  Booking,
  CancelBookingRequest,
  AdjustCheckInTimeRequest,
  AdjustCheckOutTimeRequest,
  AdjustCheckOutTimeResult,
} from '../types/booking';

export const bookingService = {
  /**
   * GET /bookings/apartments/{apartmentId}
   * Returns all bookings for the given apartment.
   */
  async getByApartment(apartmentId: string): Promise<Booking[]> {
    const response = await apiClient.get<Booking[]>(
      `/bookings/apartments/${apartmentId}`
    );
    return response.data;
  },

  /**
   * POST /bookings/{id}/cancel
   * Cancels a booking and releases the underlying availability.
   * withConflictRetry is intentionally NOT used — the cancel endpoint has no
   * optimistic lock; a 400 means the booking is already cancelled (user error).
   */
  async cancelBooking(id: string, data: CancelBookingRequest): Promise<Booking> {
    const response = await apiClient.post<Booking>(`/bookings/${id}/cancel`, data);
    return response.data;
  },

  // ── 022-checkin-checkout-times ────────────────────────────────────────────

  /**
   * POST /bookings/{id}/check-in
   * Records manual check-in. Returns the updated booking (status → CHECKED_IN).
   * No retry — check-in is idempotent at the API level; a conflict means the
   * booking is already checked in, which is a user error, not a race condition.
   */
  async checkIn(id: string): Promise<Booking> {
    const response = await apiClient.post<Booking>(`/bookings/${id}/check-in`);
    return response.data;
  },

  /**
   * POST /bookings/{id}/check-out
   * Records manual check-out. Returns the updated booking (status → COMPLETED).
   * Same no-retry rationale as checkIn.
   */
  async checkOut(id: string): Promise<Booking> {
    const response = await apiClient.post<Booking>(`/bookings/${id}/check-out`);
    return response.data;
  },

  /**
   * PATCH /bookings/{id}/check-in-time
   * Adjusts the scheduled check-in time.
   * Always HTTP 200 — returns updated booking directly.
   */
  async adjustCheckInTime(
    id: string,
    data: AdjustCheckInTimeRequest
  ): Promise<Booking> {
    const response = await apiClient.patch<Booking>(
      `/bookings/${id}/check-in-time`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /bookings/{id}/check-out-time
   * Adjusts the scheduled check-out time.
   * HTTP 200 → applied immediately (within self-service limit).
   * HTTP 202 → exceeds limit; a TimeAdjustmentInquiry is created; booking returned as-is.
   * Returns a discriminated union so callers can show the correct feedback.
   */
  async adjustCheckOutTime(
    id: string,
    data: AdjustCheckOutTimeRequest
  ): Promise<AdjustCheckOutTimeResult> {
    const response = await apiClient.patch<Booking>(
      `/bookings/${id}/check-out-time`,
      data
    );
    const type = response.status === 202 ? 'pending' : 'applied';
    return { type, booking: response.data };
  },

  // ── end 022-checkin-checkout-times ─────────────────────────────────────────

  // ── 032-booking-recovery-linking ──────────────────────────────────────────

  /**
   * GET /bookings/{bookingId}
   * Returns the full booking record for a single booking by ID.
   * Used by the Support Cases panel to derive eligible recovery actions.
   */
  async getById(bookingId: string): Promise<Booking> {
    const response = await apiClient.get<Booking>(`/bookings/${bookingId}`);
    return response.data;
  },

  /**
   * POST /api/v1/admin/bookings/{bookingId}/link-by-email
   * Manually links a booking to a guest account, resolved server-side from
   * the guest's email (the frontend never has the IdP sub).
   * 201 → success, 400 → invalid/mismatched email, 404 → booking or account
   * not found, 409 → already linked.
   */
  async linkBooking(bookingId: string, email: string): Promise<void> {
    await apiClient.post(`/admin/bookings/${bookingId}/link-by-email`, { email });
  },
};

